/**
 * BYOP (Bring Your Own Pollen) pure helpers.
 *
 * Sprint 28.17 — PR-3a Settings + BYOP. Lets users connect their own
 * Pollinations account through the OAuth-style redirect flow, then use
 * their own pollen quota for image / video / audio gen instead of the
 * operator's sk_ proxy. The Pollinations docs call this "Bring Your Own
 * Pollen": user pays for their own AI usage, app pays $0.
 *
 * Spec source:
 *   https://github.com/pollinations/pollinations/blob/main/BRING_YOUR_OWN_POLLEN.md
 *
 * Flow:
 *   1. User clicks "Connect Pollinations" in Settings
 *   2. Browser redirects to enter.pollinations.ai/authorize?... (this file
 *      builds the URL)
 *   3. User signs in on Pollinations + approves scope/budget/expiry
 *   4. Pollinations redirects back to /byop/callback with #api_key=sk_…&state=…
 *      in the URL fragment (NOT query string — never hits server logs)
 *   5. Callback page extracts the fragment (this file parses it),
 *      verifies CSRF state, and stores in byopStore (sessionStorage)
 *   6. Image/video/audio resolvers route through gen.pollinations.ai
 *      directly with the user's sk_ as Bearer auth — no server proxy
 *      needed when BYOP is connected.
 *
 * No DOM, no fetch, no store reads — pure helpers stay reusable across
 * the connect button + the callback route + future BYOP surfaces.
 */

const ENTER_BASE = 'https://enter.pollinations.ai/authorize';

/**
 * sessionStorage key for the CSRF state token used to defend the BYOP
 * redirect flow. ConnectPollinations writes a fresh `crypto.randomUUID()`
 * here before navigating to enter.pollinations.ai/authorize. The
 * /byop/callback route reads + clears it on mount and verifies the
 * `state` echoed back by Pollinations matches.
 */
export const BYOP_CSRF_KEY = 'jubuddy:byop-csrf-state';

export interface BuildAuthorizeUrlOptions {
  /**
   * Pollinations publishable App Key (`pk_…`). Optional but strongly
   * recommended — shows your app name + author on the consent screen
   * and tracks traffic for tier upgrades on the operator's account.
   * Set via `VITE_POLLINATIONS_APP_KEY` env var.
   */
  readonly clientId?: string;
  /**
   * Where Pollinations should redirect after the user
   * approves/denies. The URL fragment (`#api_key=sk_…&state=…`) is
   * appended on top of this — do NOT include a fragment yourself.
   */
  readonly redirectUri: string;
  /** Opaque CSRF token. We generate `crypto.randomUUID()` on the
   * connect button and verify it matches on the callback. */
  readonly state: string;
  /** Account access scope, e.g. 'usage' or 'usage keys'. Default
   * empty (just generation, no account access). */
  readonly scope?: string;
  /** Restrict the issued key to specific models. Comma-joined in URL. */
  readonly models?: ReadonlyArray<string>;
  /** Pollen budget cap. */
  readonly budget?: number;
  /** Key lifetime in days (default 30 per Pollinations). */
  readonly expiryDays?: number;
}

/**
 * Compose the authorize redirect URL. The user's browser navigates to
 * the returned URL; Pollinations handles consent + redirects back.
 */
export function buildAuthorizeUrl(opts: BuildAuthorizeUrlOptions): string {
  const params = new URLSearchParams();
  if (opts.clientId) params.set('client_id', opts.clientId);
  params.set('redirect_uri', opts.redirectUri);
  params.set('state', opts.state);
  if (opts.scope) params.set('scope', opts.scope);
  if (opts.models && opts.models.length > 0) {
    params.set('models', opts.models.join(','));
  }
  if (opts.budget !== undefined) params.set('budget', String(opts.budget));
  if (opts.expiryDays !== undefined) params.set('expiry', String(opts.expiryDays));
  return `${ENTER_BASE}?${params.toString()}`;
}

/**
 * Result of parsing the URL fragment after Pollinations redirects back.
 */
export type CallbackFragmentResult =
  | { readonly kind: 'success'; readonly apiKey: string; readonly state: string }
  | { readonly kind: 'denied'; readonly state: string }
  | { readonly kind: 'invalid' };

/**
 * Parse the URL fragment Pollinations leaves on the callback page. The
 * fragment is `#api_key=sk_…&state=…` on success, `#error=access_denied
 * &state=…` on denial, or empty / unrecognised on other failures.
 *
 * Browser code calls this on mount with `window.location.hash` (or
 * the equivalent) — we don't read the DOM here so the helper stays
 * environment-agnostic.
 */
export function parseCallbackFragment(fragment: string): CallbackFragmentResult {
  if (!fragment || fragment.length <= 1) return { kind: 'invalid' };
  // Strip leading '#' if present.
  const normalised = fragment.startsWith('#') ? fragment.slice(1) : fragment;
  if (normalised.length === 0) return { kind: 'invalid' };
  const params = new URLSearchParams(normalised);
  const apiKey = params.get('api_key');
  const error = params.get('error');
  const state = params.get('state') ?? '';
  if (apiKey) return { kind: 'success', apiKey, state };
  if (error === 'access_denied') return { kind: 'denied', state };
  return { kind: 'invalid' };
}

/**
 * Defensive shape check on the returned API key. A `pk_` in the
 * `api_key` slot would be a Pollinations bug, but we refuse it anyway
 * to keep the type boundary tight — the BYOP path always expects a
 * secret key.
 */
export function isValidSecretKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length > 3 && trimmed.startsWith('sk_');
}
